import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Minus,
  Plus,
  Printer,
  Search,
  Send,
  Trash2,
  Users,
  Utensils,
  Wallet,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button, Card, CardHeader, Money, StatusPill } from "@/components/ui";
import { searchProducts, type SearchHit } from "@/lib/endpoints";
import {
  addLineToCheck,
  cancelCheck,
  chargeCheck,
  getCheck,
  listModifiers,
  requestBill,
  sendToKitchen,
  voidLine,
  type PaymentPayload,
} from "@/lib/restaurant";
import { getSession } from "@/lib/session";

interface Props {
  checkId: string;
  onBack: () => void;
}

export function CheckScreen({ checkId, onBack }: Props) {
  const queryClient = useQueryClient();
  const session = getSession();
  const branchId = session?.branches?.[0]?.id ?? "";

  // Búsqueda de productos
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchHit[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<SearchHit | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [seat, setSeat] = useState<number | null>(1);
  const [course, setCourse] = useState(1);
  const [notes, setNotes] = useState("");
  const [selectedModifierIds, setSelectedModifierIds] = useState<string[]>([]);

  // Estados de cobro y diálogo
  const [showPayModal, setShowPayModal] = useState(false);
  const [payMethod, setPayMethod] = useState("efectivo");
  const [tipCents, setTipCents] = useState(0);
  const [cashGivenCents, setCashGivenCents] = useState(0);
  const [voidLineId, setVoidLineId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState("");

  // Cargar comanda
  const checkQuery = useQuery({
    queryKey: ["restaurant", "check", checkId],
    queryFn: () => getCheck(checkId),
    refetchInterval: 10_000,
  });

  // Cargar modificadores disponibles
  const modifiersQuery = useQuery({
    queryKey: ["restaurant", "modifiers"],
    queryFn: () => listModifiers(),
  });

  // Búsqueda de productos en tiempo real
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await searchProducts(searchTerm, branchId);
        setSearchResults(res.items);
      } catch (err) {
        console.error(err);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchTerm, branchId]);

  // Mutaciones
  const addLineMutation = useMutation({
    mutationFn: () => {
      if (!selectedProduct) throw new Error("Selecciona un producto");
      return addLineToCheck(checkId, {
        variant_id: selectedProduct.variant_id,
        quantity,
        modifier_ids: selectedModifierIds,
        seat,
        course,
        notes: notes || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "check", checkId] });
      queryClient.invalidateQueries({ queryKey: ["restaurant", "floor-map"] });
      setSelectedProduct(null);
      setSearchTerm("");
      setNotes("");
      setSelectedModifierIds([]);
      setQuantity(1);
    },
  });

  const sendKitchenMutation = useMutation({
    mutationFn: () => sendToKitchen(checkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "check", checkId] });
      queryClient.invalidateQueries({ queryKey: ["restaurant", "kds"] });
    },
  });

  const requestBillMutation = useMutation({
    mutationFn: () => requestBill(checkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "check", checkId] });
      queryClient.invalidateQueries({ queryKey: ["restaurant", "floor-map"] });
    },
  });

  const voidLineMutation = useMutation({
    mutationFn: () => {
      if (!voidLineId || !voidReason) throw new Error("Motivo requerido");
      return voidLine(checkId, voidLineId, voidReason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant", "check", checkId] });
      setVoidLineId(null);
      setVoidReason("");
    },
  });

  const chargeMutation = useMutation({
    mutationFn: async () => {
      const check = checkQuery.data?.check;
      if (!check) return;
      const totalConPropina = check.total_cents + tipCents;
      const payments: PaymentPayload[] = [
        {
          method: payMethod,
          amount_cents: totalConPropina,
        },
      ];
      return chargeCheck(checkId, {
        payments,
        tip_cents: tipCents,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant"] });
      setShowPayModal(false);
      onBack();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelCheck(checkId, "Cancelada por comensal o mesero"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant"] });
      onBack();
    },
  });

  if (checkQuery.isLoading) {
    return (
      <div className="text-muted flex h-96 items-center justify-center">
        Cargando comanda...
      </div>
    );
  }

  const check = checkQuery.data?.check;
  const lines = checkQuery.data?.lines ?? [];

  if (!check) {
    return (
      <div className="text-muted p-8 text-center">
        <p>No se encontró la comanda solicitada.</p>
        <Button className="mt-4" onClick={onBack}>
          Regresar al plano
        </Button>
      </div>
    );
  }

  const draftLines = lines.filter((l) => l.status === "draft");
  const totalCents = check.total_cents;
  const tipOptions = [
    { label: "Sin propina", pct: 0 },
    { label: "10%", pct: 0.1 },
    { label: "15%", pct: 0.15 },
    { label: "20%", pct: 0.2 },
  ];

  return (
    <div className="bg-surface text-carbon min-h-[calc(100vh-4rem)] space-y-4 p-4 sm:p-6">
      {/* Barra superior de la comanda */}
      <div className="border-line bg-snow flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} icon={<ArrowLeft className="size-4" />}>
            Plano
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-carbon text-lg font-bold">
                {check.label || `Comanda #${check.series}-${check.folio}`}
              </h2>
              <StatusPill tone={check.status === "open" ? "info" : "warning"}>
                {check.status === "open" ? "Abierta" : "Cuenta pedida"}
              </StatusPill>
            </div>
            <p className="text-muted flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1">
                <Users className="size-3" /> {check.guests} comensal(es)
              </span>
              <span className="flex items-center gap-1">
                <Clock className="size-3" /> {new Date(check.opened_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {draftLines.length > 0 && (
            <Button
              size="sm"
              onClick={() => sendKitchenMutation.mutate()}
              disabled={sendKitchenMutation.isPending}
              icon={<Send className="size-4" />}
            >
              Mandar a Cocina ({draftLines.length})
            </Button>
          )}

          <Button
            size="sm"
            variant="secondary"
            onClick={() => requestBillMutation.mutate()}
            disabled={requestBillMutation.isPending || lines.length === 0}
            icon={<Printer className="size-4" />}
          >
            Imprimir Cuenta
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              if (window.confirm("¿Seguro que deseas cancelar esta comanda?")) {
                cancelMutation.mutate();
              }
            }}
            disabled={cancelMutation.isPending}
            icon={<XCircle className="size-4" />}
          >
            Cancelar
          </Button>

          <Button
            size="sm"
            onClick={() => {
              setTipCents(Math.round(totalCents * 0.1));
              setCashGivenCents(totalCents + Math.round(totalCents * 0.1));
              setShowPayModal(true);
            }}
            disabled={lines.length === 0}
            icon={<Wallet className="size-4" />}
          >
            Cobrar Comanda
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Panel Izquierdo: Lista de Platillos (7 columnas) */}
        <div className="space-y-4 lg:col-span-7">
          <Card className="p-4">
            <h3 className="text-muted mb-3 text-sm font-semibold">
              Platillos y Bebidas en la Mesa ({lines.length})
            </h3>

            {lines.length === 0 ? (
              <div className="text-muted py-12 text-center">
                <Utensils className="mx-auto mb-2 size-8 opacity-40" />
                <p>La comanda aún no tiene platillos.</p>
                <p className="text-xs">Usa el buscador de la derecha para ordenar.</p>
              </div>
            ) : (
              <div className="divide-line divide-y">
                {lines.map((line) => (
                  <div key={line.id} className="flex items-start justify-between py-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-carbon font-semibold">{line.quantity}x</span>
                        <span className="text-carbon font-medium">{line.name}</span>
                        {line.seat && (
                          <span className="bg-surface text-muted border-line rounded border px-1.5 py-0.5 text-[10px] font-semibold">
                            C{line.seat}
                          </span>
                        )}
                        <span className="bg-surface text-muted rounded px-1.5 py-0.5 text-[10px]">
                          {line.course === 1 ? "Entrada" : line.course === 2 ? "Fuerte" : "Postre"}
                        </span>
                      </div>

                      {/* Modificadores */}
                      {line.modifiers && line.modifiers.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {line.modifiers.map((m, idx) => (
                            <span
                              key={idx}
                              className="bg-electric/15 text-electric-text rounded px-2 py-0.5 text-xs font-medium"
                            >
                              + {m}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Notas de comanda */}
                      {line.notes && (
                        <p className="text-warning text-xs italic font-medium">
                          Nota: {line.notes}
                        </p>
                      )}

                      {/* Estado en cocina */}
                      <div className="flex items-center gap-2 pt-1 text-xs">
                        <span
                          className={`inline-block size-2 rounded-full ${
                            line.status === "draft"
                              ? "bg-amber-500"
                              : line.status === "sent"
                              ? "bg-blue-500"
                              : line.status === "ready"
                              ? "bg-emerald-500"
                              : "bg-muted"
                          }`}
                        />
                        <span className="text-muted">
                          {line.status === "draft"
                            ? "Borrador (no mandado)"
                            : line.status === "sent"
                            ? "En cocina"
                            : line.status === "ready"
                            ? "Listo para servir"
                            : "Servido"}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-right">
                      <div>
                        <p className="text-carbon font-mono font-semibold">
                          <Money cents={line.total_cents} />
                        </p>
                        <p className="text-muted text-xs">
                          <Money cents={line.unit_price_cents} /> c/u
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setVoidLineId(line.id)}
                        className="text-muted hover:bg-danger/15 hover:text-danger rounded p-1 transition-colors"
                        title="Anular platillo"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Resumen de totales */}
            <div className="border-line mt-4 space-y-1.5 border-t pt-4">
              <div className="text-muted flex justify-between text-sm">
                <span>Subtotal</span>
                <span className="font-mono">
                  <Money cents={check.subtotal_cents} />
                </span>
              </div>
              <div className="text-muted flex justify-between text-sm">
                <span>IVA</span>
                <span className="font-mono">
                  <Money cents={check.tax_cents} />
                </span>
              </div>
              <div className="text-carbon border-line flex justify-between border-t pt-2 text-base font-bold">
                <span>Total de la cuenta</span>
                <span className="text-electric-text font-mono text-xl">
                  <Money cents={check.total_cents} />
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Panel Derecho: Búsqueda y Agregar Platillo (5 columnas) */}
        <div className="space-y-4 lg:col-span-5">
          <Card className="p-4">
            <h3 className="text-muted mb-3 text-sm font-semibold">
              Agregar a la Comanda
            </h3>

            {/* Buscador */}
            <div className="relative mb-3">
              <Search className="text-muted absolute left-3 top-2.5 size-4" />
              <input
                type="text"
                placeholder="Buscar platillo o bebida..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-line bg-surface text-carbon placeholder:text-muted focus:border-electric h-10 w-full rounded-msr border pl-9 pr-3 focus:outline-none"
              />
            </div>

            {/* Lista de resultados de búsqueda */}
            {searchResults.length > 0 && !selectedProduct && (
              <div className="divide-line border-line bg-surface mb-3 max-h-48 divide-y overflow-y-auto rounded-msr border">
                {searchResults.map((item) => (
                  <button
                    key={item.variant_id}
                    type="button"
                    onClick={() => {
                      setSelectedProduct(item);
                      setSearchTerm("");
                      setSearchResults([]);
                    }}
                    className="hover:bg-line/50 flex w-full items-center justify-between p-2.5 text-left text-sm transition-colors"
                  >
                    <div>
                      <p className="text-carbon font-medium">{item.name}</p>
                      <p className="text-muted text-xs">{item.sku}</p>
                    </div>
                    <span className="text-success font-mono font-semibold">
                      <Money cents={item.price_cents ?? 0} />
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Producto seleccionado para agregar */}
            {selectedProduct && (
              <div className="border-electric/30 bg-electric/5 space-y-4 rounded-xl border p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-carbon font-bold">{selectedProduct.name}</h4>
                    <p className="text-electric-text font-mono text-sm font-semibold">
                      <Money cents={selectedProduct.price_cents ?? 0} />
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedProduct(null)}
                  >
                    Cambiar
                  </Button>
                </div>

                {/* Cantidad y comensal */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-muted text-xs font-medium">Cantidad</label>
                    <div className="mt-1 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="border-line bg-snow text-carbon hover:bg-surface rounded border p-2"
                      >
                        <Minus className="size-3" />
                      </button>
                      <span className="text-carbon flex-1 text-center font-bold">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuantity(quantity + 1)}
                        className="border-line bg-snow text-carbon hover:bg-surface rounded border p-2"
                      >
                        <Plus className="size-3" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-muted text-xs font-medium">Asiento</label>
                    <select
                      value={seat ?? ""}
                      onChange={(e) => setSeat(e.target.value ? Number(e.target.value) : null)}
                      className="border-line bg-snow text-carbon mt-1 h-9 w-full rounded border px-2 text-xs focus:outline-none"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                        <option key={s} value={s}>
                          Comensal {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-muted text-xs font-medium">Tiempo</label>
                    <select
                      value={course}
                      onChange={(e) => setCourse(Number(e.target.value))}
                      className="border-line bg-snow text-carbon mt-1 h-9 w-full rounded border px-2 text-xs focus:outline-none"
                    >
                      <option value={1}>1. Entrada / Bebida</option>
                      <option value={2}>2. Plato Fuerte</option>
                      <option value={3}>3. Postre / Café</option>
                    </select>
                  </div>
                </div>

                {/* Modificadores (aderezos, guarniciones, etc.) */}
                {modifiersQuery.data && modifiersQuery.data.length > 0 && (
                  <div>
                    <label className="text-muted text-xs font-medium">Modificadores / Extras</label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {modifiersQuery.data.map((mod) => {
                        const isSelected = selectedModifierIds.includes(mod.id);
                        return (
                          <button
                            key={mod.id}
                            type="button"
                            onClick={() => {
                              setSelectedModifierIds(
                                isSelected
                                  ? selectedModifierIds.filter((id) => id !== mod.id)
                                  : [...selectedModifierIds, mod.id]
                              );
                            }}
                            className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                              isSelected
                                ? "bg-electric text-on-electric"
                                : "border-line bg-snow text-carbon hover:bg-surface border"
                            }`}
                          >
                            {mod.name} (+<Money cents={mod.price_delta_cents} />)
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Instrucciones de cocina */}
                <div>
                  <label className="text-muted text-xs font-medium">Instrucciones para Cocina</label>
                  <input
                    type="text"
                    placeholder="Ej. Término medio, sin cebolla, salsa aparte..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="border-line bg-snow text-carbon placeholder:text-muted focus:border-electric mt-1 h-9 w-full rounded border px-3 text-xs focus:outline-none"
                  />
                </div>

                <Button
                  className="w-full"
                  onClick={() => addLineMutation.mutate()}
                  disabled={addLineMutation.isPending}
                  icon={<Plus className="size-4" />}
                >
                  Agregar a Comanda
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Modal: Cobrar Cuenta */}
      {showPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md space-y-5 p-6 shadow-2xl">
            <CardHeader
              title="Cobro de Comanda"
              description={`Comanda ${check.series}-${check.folio} | Total a liquidar`}
            />

            {/* Total y Propina */}
            <div className="space-y-3">
              <div className="border-line bg-surface rounded-xl border p-4 text-center">
                <p className="text-muted text-xs">Total con Propina</p>
                <p className="text-carbon font-mono text-3xl font-extrabold">
                  <Money cents={totalCents + tipCents} />
                </p>
                <p className="text-muted mt-1 text-xs">
                  Consumo: <Money cents={totalCents} /> + Propina: <Money cents={tipCents} />
                </p>
              </div>

              {/* Selector de propina */}
              <div>
                <label className="text-muted text-xs font-medium">Propina para mesero</label>
                <div className="mt-1 grid grid-cols-4 gap-1.5">
                  {tipOptions.map((opt) => (
                    <button
                      key={opt.pct}
                      type="button"
                      onClick={() => setTipCents(Math.round(totalCents * opt.pct))}
                      className={`rounded-msr py-1.5 text-xs font-semibold ${
                        tipCents === Math.round(totalCents * opt.pct)
                          ? "bg-electric text-on-electric"
                          : "border-line bg-surface text-carbon border"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Método de pago */}
              <div>
                <label className="text-muted text-xs font-medium">Método de pago</label>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {[
                    { id: "efectivo", label: "Efectivo" },
                    { id: "tarjeta", label: "Tarjeta" },
                    { id: "transferencia", label: "Transferencia" },
                  ].map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPayMethod(m.id)}
                      className={`rounded-msr py-2 text-xs font-semibold ${
                        payMethod === m.id
                          ? "bg-electric text-on-electric"
                          : "border-line bg-surface text-carbon border"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Si es efectivo, calcular cambio */}
              {payMethod === "efectivo" && (
                <div>
                  <label className="text-muted text-xs font-medium">Efectivo recibido (pesos)</label>
                  <input
                    type="number"
                    value={cashGivenCents ? cashGivenCents / 100 : ""}
                    onChange={(e) => setCashGivenCents(Math.round(Number(e.target.value) * 100))}
                    placeholder="Monto entregado por cliente"
                    className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded border px-3 font-mono focus:outline-none"
                  />
                  {cashGivenCents >= totalCents + tipCents && (
                    <p className="text-success mt-1.5 text-sm font-semibold">
                      Cambio: <Money cents={cashGivenCents - (totalCents + tipCents)} />
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowPayModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => chargeMutation.mutate()}
                disabled={chargeMutation.isPending}
                icon={<CheckCircle2 className="size-4" />}
              >
                Confirmar Cobro
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal: Anular Línea */}
      {voidLineId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm space-y-4 p-6 shadow-2xl">
            <CardHeader
              title="Anular Platillo"
              description="Indica el motivo de cancelación para auditoría"
            />
            <div>
              <label className="text-muted text-xs font-medium">Motivo *</label>
              <input
                type="text"
                placeholder="Ej. Error de captura, cliente cambió de opinión"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="border-line bg-surface text-carbon focus:border-electric mt-1 h-10 w-full rounded border px-3 text-sm focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setVoidLineId(null)}>
                Regresar
              </Button>
              <Button
                variant="danger"
                onClick={() => voidLineMutation.mutate()}
                disabled={!voidReason || voidLineMutation.isPending}
              >
                Confirmar Anulación
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
